import { useCallback, useRef, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';

interface UseScrollToTabConfig {
	useOffset?: boolean;
}

interface UseScrollToTab {
	scrollContainerRef: React.RefObject<ScrollContainer>;
	tabRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
	scrollToTab: (index: number) => void;
}

export const useScrollToTab = ({ useOffset = false }: UseScrollToTabConfig = {}): UseScrollToTab => {
	const scrollContainerRef = useRef<ScrollContainer | null>(null);
	const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
	const [selectedTabIndex, setSelectedTabIndex] = useState<number | null>(null);
	const tabCloseIconAndGap = 18; // gap + close icon width

	const scrollToTab = useCallback(
		(index: number) => {
			const tab = tabRefs.current[index];
			const container = scrollContainerRef?.current?.getElement();

			if (!tab || !container) return;

			if (selectedTabIndex === index) {
				setSelectedTabIndex(null);
				return;
			}

			setSelectedTabIndex(index);

			// added setTimeout to take into account the fact that some of the tabs have additional X button
			// which is rendered after the scroll is finished resulting in visual errors.
			setTimeout(() => {
				const tabOffsetLeft = tab.offsetLeft;
				const containerWidth = container.clientWidth;
				const maxScroll = container.scrollWidth - containerWidth;
				const isScrollingRight = selectedTabIndex !== null && index > selectedTabIndex;
				const isLastTab = index === tabRefs.current.length - 1;

				const returnScrollTarget = () => {
					if (selectedTabIndex === null && isLastTab) {
						return maxScroll;
					}
					if (useOffset) {
						return isScrollingRight ? tabOffsetLeft - tabCloseIconAndGap : tabOffsetLeft;
					}

					return tabOffsetLeft;
				};

				container.scrollTo({
					left: returnScrollTarget(),
					behavior: 'smooth',
				});
			}, 0);
		},
		[selectedTabIndex],
	);

	return { scrollContainerRef, tabRefs, scrollToTab };
};
